import * as THREE from "three";
import { inked, toon } from "../toon";
import type { SkaterLook } from "./look";
import { facets } from "./geom";

// Roller skates (#140): aggressive inlines, one on each foot. Each is built
// in the foot's own space (toe toward -x, like the shoes): a cuff round the
// ankle, a frame under the sole with four small wheels in a line, and a
// soul plate on the outside of the frame. A third pair, `carried`, is the
// skates slung over the shoulder by their laces while you walk (#145): one
// hanging down the chest, one down the back. Built only once someone puts
// them on.

/** Wheel radius (m) and the four wheels' places along the frame (x, toe at -x). */
export const SKATE_WHEEL_R = 0.03;
const WHEEL_X = [-0.1, -0.035, 0.035, 0.1] as const;
/** How far below the foot's origin the wheels touch the ground (m): the shoe's sole plus the frame. */
export const SKATE_FOOT_Y = 0.12;

export interface Skates {
  /** One per leg, a child of that leg's foot; and every wheel, to turn as it rolls. */
  worn: THREE.Group[];
  wheels: THREE.Object3D[];
  /** The pair slung by the laces, in the yaw group's space; its origin is where the laces go over the shoulder, its children the two skates. */
  carried: THREE.Group;
}

function buildSkate(frameMat: THREE.Material, cuffMat: THREE.Material, wheelMat: THREE.Material, wheels: THREE.Object3D[]): THREE.Group {
  const skate = new THREE.Group();
  skate.name = "skate";
  const y = -SKATE_FOOT_Y + SKATE_WHEEL_R;
  const frame = inked(new THREE.BoxGeometry(0.26, 0.035, 0.035), frameMat, 0.008);
  frame.position.set(0, y + 0.02, 0);
  // The soul plate: a flat block out on the frame's side, under the arch.
  const plate = inked(new THREE.BoxGeometry(0.09, 0.03, 0.03), frameMat, 0.006);
  plate.position.set(0.01, y + 0.025, 0.045);
  const cuff = inked(new THREE.CylinderGeometry(0.06, 0.065, 0.09, facets(12)), cuffMat, 0.01);
  cuff.position.set(0.06, 0.08, 0);
  skate.add(frame, plate, cuff);
  for (const x of WHEEL_X) {
    const w = inked(new THREE.CylinderGeometry(SKATE_WHEEL_R, SKATE_WHEEL_R, 0.022, facets(12)), wheelMat, 0.005);
    w.rotation.x = Math.PI / 2;
    const hub = new THREE.Group();
    hub.position.set(x, y, 0);
    hub.add(w);
    skate.add(hub);
    wheels.push(hub);
  }
  return skate;
}

/** Slung (#145): how far each skate hangs below the laces, in front of or behind the shoulder, and out from it (m). */
const SLING_DROP = 0.1;
const SLING_Z = 0.13;
const SLING_X = 0.08;

export function buildSkates(look: SkaterLook): Skates {
  const frameMat = toon("#2b2833", { roughness: 0.5 }, "plastic");
  const cuffMat = toon(look.deck, { roughness: 0.45 }, "plastic");
  const wheelMat = toon(look.wheels, { roughness: 0.4 }, "plastic");
  const wheels: THREE.Object3D[] = [];
  const worn = [0, 1].map(() => buildSkate(frameMat, cuffMat, wheelMat, wheels));
  const carried = new THREE.Group();
  carried.name = "skates-carried";
  // Hung toe down from the laces, one in front of the shoulder and one behind.
  for (const side of [1, -1]) {
    const s = buildSkate(frameMat, cuffMat, wheelMat, []);
    s.rotation.z = Math.PI / 2;
    s.position.set(SLING_X, -SLING_DROP, side * SLING_Z);
    carried.add(s);
  }
  // The laces, tied cuff to cuff over the shoulder.
  const laces = inked(new THREE.BoxGeometry(0.012, 0.012, 2 * SLING_Z), frameMat, 0.003);
  laces.position.set(0, 0.01, 0);
  carried.add(laces);
  return { worn, wheels, carried };
}
